import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Loader2, Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPeriod(r) {
  if (r.period_label) return r.period_label;
  if (r.period_year && r.period_month) {
    try {
      return new Date(r.period_year, r.period_month - 1, 1)
        .toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } catch { /* noop */ }
  }
  return '—';
}

const STATUS_TONE = {
  generated: 'bg-sky-50 text-sky-700 border-sky-200',
  negative_balance: 'bg-rose-50 text-rose-700 border-rose-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  partially_paid: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  payment_reversed: 'bg-orange-50 text-orange-700 border-orange-200',
};

const STATUS_LABEL = {
  generated: 'Issued',
  negative_balance: 'Owed to KR',
  pending_payment: 'Pending payment',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  payment_reversed: 'Reversed',
};

/**
 * Executive statement ledger for an Agent / Agency profile. Lists every
 * statement (positive, negative and zero) with its period, carrier, net
 * amount and a one-click PDF. Payout-side only — the confidential
 * "KR Received" figure is never surfaced here.
 */
export default function RecipientStatementsCard({ recipientType, recipientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    if (!recipientType || !recipientId) return;
    setLoading(true);
    try {
      const { data } = await accountingAPI.recipientStatements(recipientType, recipientId);
      setRows(data.items || []);
    } catch { /* silent — non-owners simply see nothing */ }
    finally { setLoading(false); }
  }, [recipientType, recipientId]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (row) => {
    if (!row?.statement_id) return;
    setDownloadingId(row.statement_id);
    try {
      const res = await accountingAPI.downloadStatementPdf(row.statement_id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.statement_number || row.statement_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const status = e.response?.status;
      toast.error(status === 404 ? 'PDF not available for this statement' : 'Failed to download statement');
    } finally { setDownloadingId(null); }
  };

  return (
    <Card data-testid="recipient-statements-card" className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-sky-600" /> Statements
        </CardTitle>
        {!loading && rows.length > 0 && (
          <span className="text-xs text-slate-500">{rows.length} statement{rows.length === 1 ? '' : 's'}</span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="statements-empty">No statements issued yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Statement</th>
                  <th className="text-left px-3 py-2 font-medium">Period</th>
                  <th className="text-left px-3 py-2 font-medium">Carrier</th>
                  <th className="text-right px-3 py-2 font-medium">Net amount</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-center px-2 py-2 font-medium">PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const net = Number(r.net_total || 0);
                  const Icon = net > 0 ? TrendingUp : net < 0 ? TrendingDown : Minus;
                  const netTone = net > 0 ? 'text-emerald-700' : net < 0 ? 'text-rose-700' : 'text-slate-500';
                  return (
                    <tr key={r.statement_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors"
                        data-testid={`stmt-row-${r.statement_id}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{r.statement_number || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-700">{fmtPeriod(r)}</td>
                      <td className="px-3 py-2.5 text-slate-700">{r.carrier_name || '—'}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold ${netTone}`}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          <Icon className="h-3.5 w-3.5" /> {money(net)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge className={`${STATUS_TONE[r.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border font-normal`}>
                          {STATUS_LABEL[r.status] || (r.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <Button
                          type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => downloadPdf(r)}
                          disabled={downloadingId === r.statement_id}
                          data-testid={`stmt-pdf-${r.statement_id}`}
                          title={`Download ${r.statement_number || 'statement'} PDF`}
                        >
                          {downloadingId === r.statement_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Download className="h-3.5 w-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
